import { useId } from 'react';
import { SAC_CODES, findSac, suggestSac } from '@fint/validation';
import { Input, type InputProps } from '@/features/common/ui';

/**
 * The HSN/SAC box, with the SAC list behind it and a suggestion from the name.
 *
 * ── WHY A DATALIST AND NOT A DROPDOWN ──
 * The box has to take anything: a six-digit SAC from the list, a four-digit
 * heading, or an HSN for goods that no list here carries. A `<datalist>` is
 * exactly that - a text box that offers completions and refuses nothing -
 * and the browser draws it on a phone as well as it does on a laptop. Typing
 * "9983" shows the seven IT and consulting headings; typing "hosting" finds
 * 998315 by its wording, because the options carry the title.
 *
 * ── THE SUGGESTION ──
 * When the box is empty and the item's name places it - "Website
 * development" is 998314 - the suggestion is shown WITH its wording and a
 * one-press "Use", never filled in silently: the person confirms the
 * classification, because a confident wrong code reads exactly like a right
 * one on a return. Once anything is typed the suggestion steps aside; a code
 * that is in the list shows its wording under the box so a typo in the last
 * digit is a visibly different service.
 */
export function HsnSacField({
  value,
  onChange,
  nameForSuggestion,
  label = 'HSN/SAC',
  hint,
  compact,
  ...props
}: Omit<InputProps, 'value' | 'onChange' | 'list'> & {
  value: string;
  onChange: (next: string) => void;
  /** The item or line name, read for a suggestion while the box is empty. */
  nameForSuggestion?: string | null;
  /** In a document line: no label, the wording as the placeholder. */
  compact?: boolean;
}) {
  const listId = useId();
  const trimmed = value.trim();
  const known = findSac(trimmed);
  const suggestion = trimmed ? null : suggestSac(nameForSuggestion);

  const wording = known
    ? `${known.code} · ${known.title}`
    : suggestion
      ? `Suggested: ${suggestion.code} · ${suggestion.title}`
      : hint;

  return (
    <div className="flex flex-col gap-1">
      <Input
        {...props}
        label={compact ? undefined : label}
        aria-label={compact ? label : undefined}
        placeholder={compact ? 'HSN or SAC' : props.placeholder}
        list={listId}
        inputMode="numeric"
        autoComplete="off"
        className={compact ? `font-mono ${props.className ?? ''}` : props.className}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        hint={compact ? undefined : wording}
      />
      <datalist id={listId}>
        {SAC_CODES.map((entry) => (
          <option key={entry.code} value={entry.code} label={entry.title} />
        ))}
      </datalist>
      {suggestion ? (
        <p className={compact ? 'text-xs text-muted-foreground' : '-mt-1 text-xs'}>
          {compact ? `Suggested ${suggestion.code} · ${suggestion.title} — ` : null}
          <button
            type="button"
            className="font-medium text-primary underline-offset-2 hover:underline touch:min-h-11"
            onClick={() => onChange(suggestion.code)}
            data-testid="hsn-use-suggestion"
          >
            Use {suggestion.code}
          </button>
        </p>
      ) : compact && known ? (
        <p className="text-xs text-muted-foreground">{known.title}</p>
      ) : null}
    </div>
  );
}
